import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Popper,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";

const AOISelector = ({ onAOISelected, AOIData }) => {
  const [selectedAOIs, setSelectedAOIs] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const popperRef = useRef(null);

  useEffect(() => {
    const storedAOIs = sessionStorage.getItem("selectedAOIs");
    if (storedAOIs) {
      const parsedAOIs = JSON.parse(storedAOIs);
      setSelectedAOIs(parsedAOIs);
      onAOISelected(parsedAOIs);
    }
  }, []);

  const handleAOIToggle = (aoiId) => {
    const newSelectedAOIs = selectedAOIs.includes(aoiId)
      ? selectedAOIs.filter((id) => id !== aoiId)
      : [...selectedAOIs, aoiId];
    setSelectedAOIs(newSelectedAOIs);
    sessionStorage.setItem("selectedAOIs", JSON.stringify(newSelectedAOIs));
    onAOISelected(newSelectedAOIs);
  };

  const handleDropdownClick = (event) => {
    setAnchorEl(event.currentTarget);
    setShowDropdown((prev) => !prev);
  };

  const handleClickAway = (event) => {
    if (
      anchorEl &&
      (anchorEl.contains(event.target) || popperRef.current?.contains(event.target))
    ) {
      return;
    }
    setShowDropdown(false);
  };

  const isAOIActive = selectedAOIs.length > 0;

  return (
    <Box
      sx={{
        gap: "2px",
        display: "flex",
        flexDirection: "column",
        color: "rgba(0, 0, 0, 0.6)",
      }}
    >
      <Typography
        variant="h7"
        sx={{
          marginLeft: "2px",
        }}
      >
        Area of Interest
      </Typography>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>
          <Box
            className="aoi-selector"
            onClick={handleDropdownClick}
            sx={{
              cursor: "pointer",
              color: isAOIActive ? "primary.main" : "rgba(0, 0, 0, 0.87)",
              border: "2px solid",
              borderColor: isAOIActive ? "primary.main" : "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              padding: "8px",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              minHeight: "56px",
              minWidth: "170px",
              display: "flex",
              alignItems: "center",
              transition: "all 0.3s ease",
              backgroundColor: isAOIActive
                ? "rgba(0, 0, 0, 0.04)"
                : "transparent",
            }}
          >
            <Box display="flex" alignItems="center">
              <MapIcon
                sx={{ mr: 1, color: isAOIActive ? "primary.main" : "inherit" }}
              />
              <Typography sx={{ fontWeight: isAOIActive ? "bold" : "normal" }}>
                {selectedAOIs.length === 0
                  ? "Select AOI"
                  : selectedAOIs.length === 1
                  ? AOIData.find((aoi) => aoi.id === selectedAOIs[0])?.properties
                      .name
                  : `${selectedAOIs.length} AOIs selected`}
              </Typography>
            </Box>
          </Box>
          <Popper
            open={showDropdown}
            anchorEl={anchorEl}
            placement="bottom-start"
            ref={popperRef}
            style={{ zIndex: 2000 }}
          >
            <Box
              sx={{
                width: "200px",
                mt: 1,
                boxShadow: 3,
                borderRadius: 1,
                bgcolor: "background.paper",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              <List dense>
                {AOIData.map((aoi) => (
                  <ListItem
                    key={aoi.id}
                    dense
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAOIToggle(aoi.id);
                    }}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedAOIs.includes(aoi.id)}
                      tabIndex={-1}
                      disableRipple
                      sx={{
                        color: "primary.main",
                        "&.Mui-checked": {
                          color: "primary.main",
                        },
                      }}
                    />
                    <ListItemText primary={aoi.properties.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Popper>
        </div>
      </ClickAwayListener>
    </Box>
  );
};

export default AOISelector